// skills/bounty/bounty.js
// Bounty skill logic - contract tracking and completion

import { PLAYER_CONTRACTS, CONTRACT_TIERS, BOUNTY_CONFIG, generateContractInstance, getBountyXP, getBountyMark } from './bountyData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { showGoldGain } from '../../ui/floatingNotifications.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import { getTalentFlatBonus, getTalentProcChance, rollTalentProc, getTalentXPBonus, getMasteryMultiplier } from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';

class BountySkill {
    constructor() {
        // Callbacks for UI updates
        this.onContractAccepted = null;
        this.onContractDelivered = null;
        this.onContractAbandoned = null;
        this.onContractReady = null;

        // Cache for generated contract instances
        this._generatedContracts = {};

        // Track which contracts have already triggered "ready" notification
        this._notifiedReadyContracts = new Set();
    }

    // ==================== CONTRACT GENERATION ====================

    /**
     * Get or generate a contract instance with randomized targets
     * @param {string} contractKey - Contract key from PLAYER_CONTRACTS
     * @returns {Object} Generated contract instance
     */
    getOrGenerateContract(contractKey) {
        const baseContract = PLAYER_CONTRACTS[contractKey];
        if (!baseContract) return null;

        // Return cached instance if exists (contracts persist until delivered)
        const cached = this._generatedContracts[contractKey];
        if (cached) {
            return cached;
        }

        // Generate new instance
        const instance = generateContractInstance(baseContract);
        this._generatedContracts[contractKey] = instance;
        return instance;
    }

    /**
     * Regenerate all contracts (for refresh button or timer)
     */
    refreshAllContracts() {
        this._generatedContracts = {};
        logger.game('Bounty contracts refreshed with new targets');
    }

    // ==================== CONTRACT QUERIES ====================

    /**
     * Check if a contract's conditions are met
     * @param {Object} contract - Contract definition
     * @returns {boolean} Whether all conditions are satisfied
     */
    meetsContractConditions(contract) {
        if (!contract.condition) return true;

        const { condition } = contract;

        // Check quest completion
        if (condition.questComplete !== undefined) {
            if (!gameState.isQuestCompleted(condition.questComplete)) {
                return false;
            }
        }

        // Check quest unlock (e.g., zone unlocks)
        if (condition.questUnlock) {
            if (!gameState.hasQuestUnlock(condition.questUnlock)) {
                return false;
            }
        }

        // Check pyre level
        if (condition.pyreLevel !== undefined) {
            const playerPyreLevel = gameState.getSkill(SKILLS.PYRE)?.level || 1;
            if (playerPyreLevel < condition.pyreLevel) {
                return false;
            }
        }

        // Check if boss has been defeated at least once
        if (condition.bossDefeated) {
            if (!gameState.hasDefeatedBoss(condition.bossDefeated)) {
                return false;
            }
        }

        // Check if ANY of multiple bosses have been defeated
        if (condition.anyBossDefeated && Array.isArray(condition.anyBossDefeated)) {
            const hasDefeatedAny = condition.anyBossDefeated.some(id => gameState.hasDefeatedBoss(id));
            if (!hasDefeatedAny) {
                return false;
            }
        }

        // Check if ALL of multiple bosses have been defeated
        if (condition.allBossesDefeated && Array.isArray(condition.allBossesDefeated)) {
            const hasDefeatedAll = gameState.hasDefeatedAllBosses(condition.allBossesDefeated);
            if (!hasDefeatedAll) {
                return false;
            }
        }

        return true;
    }

    /**
     * Get all contracts available at the player's bounty level (respects conditions)
     * @returns {Object[]} Array of available contracts (generated instances)
     */
    getAvailableContracts() {
        const skill = gameState.getSkill(SKILLS.BOUNTY);
        const bountyLevel = skill.level;

        return Object.entries(PLAYER_CONTRACTS)
            .filter(([key, contract]) => {
                const tierConfig = CONTRACT_TIERS[contract.tier];
                if (!tierConfig || bountyLevel < tierConfig.bountyLevel) return false;

                // Check conditional requirements
                return this.meetsContractConditions(contract);
            })
            .map(([key]) => this.getOrGenerateContract(key));
    }

    /**
     * Get contracts filtered by tier (respects conditions)
     * @param {number} tier - Contract tier (1-5)
     * @returns {Object[]} Array of contracts in that tier that the player can access
     */
    getContractsByTier(tier) {
        return Object.values(PLAYER_CONTRACTS)
            .filter(c => c.tier === tier && this.meetsContractConditions(c));
    }

    /**
     * Get all active contracts for the player
     * @returns {Object[]} Array of active contract data
     */
    getActiveContracts() {
        return gameState.getBountyContracts();
    }

    /**
     * Get a specific active contract by ID
     * @param {string} contractId - Contract ID
     * @returns {Object|null} Active contract data or null
     */
    getActiveContract(contractId) {
        const contracts = this.getActiveContracts();
        return contracts.find(c => c.contractId === contractId) || null;
    }

    /**
     * Check if a contract is currently active
     * @param {string} contractId - Contract ID
     * @returns {boolean}
     */
    isContractActive(contractId) {
        return this.getActiveContract(contractId) !== null;
    }

    // ==================== CONTRACT MANAGEMENT ====================

    /**
     * Check if the player can accept a specific contract
     * @param {string} contractId - Contract ID
     * @returns {Object} { success, reason }
     */
    canAcceptContract(contractId) {
        // Get the generated contract instance
        const contract = this.getOrGenerateContract(contractId.toUpperCase());
        if (!contract) {
            return { success: false, reason: 'Invalid contract' };
        }

        // Check if already active
        if (this.isContractActive(contract.id)) {
            return { success: false, reason: 'Contract already active' };
        }

        // Check bounty level requirement
        const skill = gameState.getSkill(SKILLS.BOUNTY);
        const tierConfig = CONTRACT_TIERS[contract.tier];
        if (!tierConfig || skill.level < tierConfig.bountyLevel) {
            return { success: false, reason: `Requires Bounty level ${tierConfig?.bountyLevel || '?'}` };
        }

        // Check max active contracts
        const activeContracts = this.getActiveContracts();
        if (activeContracts.length >= BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS) {
            return { success: false, reason: `Max ${BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS} active contracts` };
        }

        // Check if player can afford the contract cost
        const cost = tierConfig.cost || 0;
        if (cost > 0 && gameState.getGold() < cost) {
            return { success: false, reason: `Need ${cost.toLocaleString()} gold to accept` };
        }

        return { success: true, contract, cost };
    }

    /**
     * Check if Mega Contracts talent is unlocked
     * @returns {boolean}
     */
    hasMegaContracts() {
        return getTalentFlatBonus(SKILLS.BOUNTY, 'megaContracts') > 0;
    }

    /**
     * Check if Dedicated Supplier talent is unlocked
     * @returns {boolean}
     */
    hasDedicatedSupplier() {
        return getTalentFlatBonus(SKILLS.BOUNTY, 'dedicatedSupplier') > 0;
    }

    /**
     * Pin a contract (Dedicated Supplier talent)
     * @param {string} contractId - Contract ID to pin
     * @returns {Object} { success, reason }
     */
    pinContract(contractId) {
        if (!this.hasDedicatedSupplier()) {
            return { success: false, reason: 'Dedicated Supplier talent required' };
        }

        const contract = PLAYER_CONTRACTS[contractId.toUpperCase()];
        if (!contract) {
            return { success: false, reason: 'Invalid contract' };
        }

        gameState.setPinnedContract(contractId.toUpperCase());
        logger.game(`Pinned contract: ${contract.name}`);
        return { success: true, contract };
    }

    /**
     * Unpin the current pinned contract
     * @returns {Object} { success }
     */
    unpinContract() {
        gameState.clearPinnedContract();
        logger.game('Unpinned contract');
        return { success: true };
    }

    /**
     * Check if a contract is pinned
     * @param {string} contractId - Contract ID to check
     * @returns {boolean}
     */
    isPinned(contractId) {
        const pinned = gameState.getPinnedContract();
        return pinned && pinned.toUpperCase() === contractId.toUpperCase();
    }

    /**
     * Get the pinned contract ID
     * @returns {string|null}
     */
    getPinnedContractId() {
        return gameState.getPinnedContract();
    }

    /**
     * Accept a contract and add it to active contracts
     * @param {string} contractId - Contract ID
     * @param {boolean} isMega - Accept as mega contract (5x req, 7x reward)
     * @returns {Object} { success, reason }
     */
    acceptContract(contractId, isMega = false) {
        const canAccept = this.canAcceptContract(contractId);
        if (!canAccept.success) {
            return canAccept;
        }

        // Validate mega contract request
        if (isMega && !this.hasMegaContracts()) {
            return { success: false, reason: 'Mega Contracts talent required' };
        }

        const contract = canAccept.contract;
        let cost = canAccept.cost || 0;

        // Mega contracts cost 5x
        if (isMega) {
            cost = cost * 5;
            // Re-check gold for mega cost
            if (cost > 0 && gameState.getGold() < cost) {
                return { success: false, reason: `Need ${cost.toLocaleString()} gold for mega contract` };
            }
        }

        // Deduct contract cost (gold sink mechanic)
        if (cost > 0) {
            gameState.removeGold(cost);
            logger.game(`Paid ${cost.toLocaleString()} gold for ${isMega ? 'mega ' : ''}contract`);
        }

        // Apply Efficient Tracker talent (requirement reduction)
        const reqReduction = getTalentFlatBonus(SKILLS.BOUNTY, 'contractRequirementReduction');

        // Apply Specialized Reduction (-10% for most-completed type)
        const specializedReduction = getTalentFlatBonus(SKILLS.BOUNTY, 'specializedReduction');
        const mostCompletedType = specializedReduction > 0 ? this.getMostCompletedContractType() : null;
        const isSpecialized = mostCompletedType && contract.type === mostCompletedType;

        // Mega contracts have 5x requirements
        const megaMultiplier = isMega ? 5 : 1;

        const adjustedRequirements = contract.requirements.map(req => {
            let target = req.target * megaMultiplier;
            if (reqReduction > 0 && target > 1) {
                target = Math.max(1, Math.floor(target * (1 - reqReduction)));
            }
            if (isSpecialized && target > 1) {
                target = Math.max(1, Math.floor(target * (1 - specializedReduction)));
            }
            return { ...req, target };
        });

        // Mega contracts have 7x rewards
        const rewardMultiplier = isMega ? 7 : 1;
        const adjustedRewards = {
            gold: Math.floor((contract.rewards.gold || 0) * rewardMultiplier),
            xp: {}
        };
        if (contract.rewards.xp) {
            for (const [skillId, xp] of Object.entries(contract.rewards.xp)) {
                adjustedRewards.xp[skillId] = Math.floor(xp * rewardMultiplier);
            }
        }

        // Create active contract with fresh progress tracking
        // Store the generated requirements with their specific targets
        const activeContract = {
            contractId: contract.id,
            tier: contract.tier,
            type: contract.type,
            acceptedAt: Date.now(),
            progress: {},
            // Store starting inventory counts so we only count items gathered AFTER accepting
            startingInventory: {},
            // Store the adjusted requirements (with reduction applied)
            requirements: adjustedRequirements,
            rewards: adjustedRewards,
            isMega: isMega
        };

        // Initialize progress for each requirement
        // For items, store current inventory so we only count NEW items gathered
        for (const req of adjustedRequirements) {
            if (req.itemId) {
                activeContract.progress[req.itemId] = 0;
                activeContract.startingInventory[req.itemId] = gameState.getItemCount(req.itemId);
            } else if (req.mobId) {
                activeContract.progress[req.mobId] = 0;
            }
        }

        // Add to active contracts
        gameState.addBountyContract(activeContract);

        logger.game(`Accepted contract: ${contract.name}`);

        if (this.onContractAccepted) {
            this.onContractAccepted(contract);
        }

        return { success: true, contract };
    }

    /**
     * Get current abandon count
     * @returns {number} Number of contracts abandoned since last completion
     */
    getAbandonCount() {
        return gameState.player.bountyAbandonCount || 0;
    }

    /**
     * Increment abandon count
     */
    incrementAbandonCount() {
        gameState.player.bountyAbandonCount = this.getAbandonCount() + 1;
    }

    /**
     * Reset abandon count (called on successful delivery)
     */
    resetAbandonCount() {
        gameState.player.bountyAbandonCount = 0;
    }

    /**
     * Check if player can abandon a contract
     * @returns {Object} { canAbandon, reason, remaining }
     */
    canAbandonContract() {
        const abandonCount = this.getAbandonCount();
        const maxAbandons = BOUNTY_CONFIG.MAX_ABANDONS_BEFORE_COMPLETE;

        if (abandonCount >= maxAbandons) {
            return {
                canAbandon: false,
                reason: 'Must complete a contract before abandoning more',
                remaining: 0
            };
        }

        return {
            canAbandon: true,
            remaining: maxAbandons - abandonCount
        };
    }

    /**
     * Abandon an active contract without rewards
     * @param {string} contractId - Contract ID
     * @returns {Object} { success, reason }
     */
    abandonContract(contractId) {
        const activeContract = this.getActiveContract(contractId);
        if (!activeContract) {
            return { success: false, reason: 'Contract not active' };
        }

        // Check abandon limit
        const abandonCheck = this.canAbandonContract();
        if (!abandonCheck.canAbandon) {
            return { success: false, reason: abandonCheck.reason };
        }

        const contract = PLAYER_CONTRACTS[contractId.toUpperCase()];
        gameState.removeBountyContract(contractId);
        this.clearReadyNotification(contractId);

        // Increment abandon count
        this.incrementAbandonCount();

        // Reset reputation streak on abandon
        this.resetReputationStreak();

        logger.game(`Abandoned contract: ${contract?.name || contractId} (${this.getAbandonCount()}/${BOUNTY_CONFIG.MAX_ABANDONS_BEFORE_COMPLETE} abandons used)`);

        if (this.onContractAbandoned) {
            this.onContractAbandoned(contract);
        }

        return { success: true };
    }

    /**
     * Check if all requirements for a contract are met
     * @param {string} contractId - Contract ID
     * @returns {Object} { success, reason, details }
     */
    canDeliverContract(contractId) {
        const activeContract = this.getActiveContract(contractId);
        if (!activeContract) {
            return { success: false, reason: 'Contract not active' };
        }

        // Use stored requirements from active contract (has generated targets)
        const requirements = activeContract.requirements;
        if (!requirements) {
            return { success: false, reason: 'Invalid contract data' };
        }

        // Check each requirement
        const details = [];
        let allMet = true;

        for (const req of requirements) {
            if (req.itemId) {
                // Use progress counter for tracking, but also verify player has items to hand in
                const gathered = activeContract.progress[req.itemId] || 0;
                const need = req.target;
                // Check player actually has enough items (inventory + BloomHold)
                let available = gameState.getItemCount(req.itemId);
                if (gameState.isBloomholdUnlocked() && isBloomholdEligible(req.itemId)) {
                    available += gameState.getBloomholdItemCount(req.itemId);
                }
                const met = gathered >= need && available >= need;
                details.push({ type: 'item', id: req.itemId, have: gathered, need, met });
                if (!met) allMet = false;
            } else if (req.mobId) {
                // Combat tracking would go here
                const killed = activeContract.progress[req.mobId] || 0;
                const need = req.target;
                const met = killed >= need;
                details.push({ type: 'kill', id: req.mobId, have: killed, need, met });
                if (!met) allMet = false;
            }
        }

        if (!allMet) {
            return { success: false, reason: 'Requirements not met', details };
        }

        // Get display name from base contract
        const baseContract = PLAYER_CONTRACTS[contractId.toUpperCase()];
        return { success: true, contract: { ...baseContract, ...activeContract }, details };
    }

    /**
     * Get current reputation streak count
     * @returns {number} Current streak (0-10, representing 0-20% bonus)
     */
    getReputationStreak() {
        return gameState.player.bountyStreak || 0;
    }

    /**
     * Increment reputation streak (called on successful delivery)
     */
    incrementReputationStreak() {
        const hasStreakTalent = getTalentFlatBonus(SKILLS.BOUNTY, 'reputationStreak') > 0;
        if (!hasStreakTalent) return;

        const currentStreak = this.getReputationStreak();
        const maxStreak = 10; // 10 stacks × 2% = 20% max
        if (currentStreak < maxStreak) {
            gameState.player.bountyStreak = currentStreak + 1;
            logger.game(`Reputation Streak: ${currentStreak + 1}/10 (+${(currentStreak + 1) * 2}% rewards)`);
        }
    }

    /**
     * Reset reputation streak (called on abandon)
     */
    resetReputationStreak() {
        if (gameState.player.bountyStreak > 0) {
            gameState.player.bountyStreak = 0;
            logger.game('Reputation Streak reset!');
        }
    }

    /**
     * Get the bounty mark multiplier for a contract
     * Kill contracts get bonus based on mob's bounty mark rank
     * @param {Object} activeContract - The active contract instance
     * @returns {number} Multiplier (1.0 for non-kill or unranked)
     */
    getMarkMultiplier(activeContract) {
        if (activeContract.type !== 'kill') return 1;
        const mobId = activeContract.requirements?.[0]?.mobId;
        if (!mobId) return 1;
        const mark = getBountyMark(mobId);
        return 1 + mark.bountyBonus;
    }

    /**
     * Increment contract type completion counter (for Specialized Reduction)
     * @param {string} type - Contract type ('gathering', 'processing', 'kill')
     */
    incrementTypeCompletion(type) {
        if (!type) return;
        if (!gameState.player.bountyTypeCompletions) {
            gameState.player.bountyTypeCompletions = {};
        }
        gameState.player.bountyTypeCompletions[type] = (gameState.player.bountyTypeCompletions[type] || 0) + 1;
    }

    /**
     * Get the most-completed contract type for Specialized Reduction talent
     * @returns {string|null} The type with the most completions, or null if none
     */
    getMostCompletedContractType() {
        const completions = gameState.player.bountyTypeCompletions;
        if (!completions) return null;

        let maxType = null;
        let maxCount = 0;
        for (const [type, count] of Object.entries(completions)) {
            if (count > maxCount) {
                maxCount = count;
                maxType = type;
            }
        }
        return maxType;
    }

    /**
     * Get random surplus material for Surplus Finder talent, scaled by contract tier
     * @param {number} tier - Contract tier (1-5)
     * @returns {Object} { itemId, quantity }
     */
    getSurplusMaterial(tier = 1) {
        // Tier-based material pools with appropriate items
        const tierPools = {
            1: [
                { itemId: 'pyrestone_bar', weight: 4 },
                { itemId: 'pale_shard_bar', weight: 3 },
                { itemId: 'treated_hide', weight: 4 },
                { itemId: 'linenweave', weight: 3 },
                { itemId: 'spun_fiber', weight: 4 },
            ],
            2: [
                { itemId: 'pale_shard_bar', weight: 3 },
                { itemId: 'blackiron_bar', weight: 4 },
                { itemId: 'gold_bar', weight: 3 },
                { itemId: 'green_wyrm_hide', weight: 3 },
                { itemId: 'silkweave', weight: 3 },
                { itemId: 'spun_fiber', weight: 2 },
            ],
            3: [
                { itemId: 'blackiron_bar', weight: 2 },
                { itemId: 'gold_bar', weight: 3 },
                { itemId: 'skyiron_bar', weight: 4 },
                { itemId: 'blue_wyrm_hide', weight: 3 },
                { itemId: 'aetherweave', weight: 3 },
            ],
            4: [
                { itemId: 'skyiron_bar', weight: 3 },
                { itemId: 'verdant_core_bar', weight: 4 },
                { itemId: 'red_wyrm_hide', weight: 3 },
                { itemId: 'runebound_cloth', weight: 3 },
            ],
            5: [
                { itemId: 'verdant_core_bar', weight: 3 },
                { itemId: 'voidsteel_bar', weight: 4 },
                { itemId: 'black_drake_hide', weight: 3 },
                { itemId: 'shadowweave', weight: 3 },
            ]
        };

        const materials = tierPools[tier] || tierPools[1];

        // Quantity scales with tier: T1=1-2, T2=2-3, T3=3-4, T4=4-5, T5=5-7
        const minQty = tier;
        const maxQty = tier + Math.min(tier, 2);
        const quantity = Math.floor(Math.random() * (maxQty - minQty + 1)) + minQty;

        // Weighted random selection
        const totalWeight = materials.reduce((sum, m) => sum + m.weight, 0);
        let roll = Math.random() * totalWeight;
        for (const mat of materials) {
            roll -= mat.weight;
            if (roll <= 0) {
                return { itemId: mat.itemId, quantity };
            }
        }
        return { itemId: materials[0].itemId, quantity }; // Fallback
    }

    /**
     * Deliver a completed contract, consuming items and awarding rewards
     * @param {string} contractId - Contract ID
     * @returns {Object} { success, reason, rewards }
     */
    deliverContract(contractId) {
        const canDeliver = this.canDeliverContract(contractId);
        if (!canDeliver.success) {
            return canDeliver;
        }

        const activeContract = this.getActiveContract(contractId);
        const contract = canDeliver.contract;

        // Consume required items (inventory first, then BloomHold for eligible items)
        for (const req of activeContract.requirements) {
            if (req.itemId) {
                let remaining = req.target;

                // Try inventory first
                const inventoryCount = gameState.getItemCount(req.itemId);
                if (inventoryCount > 0) {
                    const fromInventory = Math.min(remaining, inventoryCount);
                    gameState.removeItem(req.itemId, fromInventory);
                    remaining -= fromInventory;
                }

                // Pull remainder from BloomHold if eligible and available
                if (remaining > 0 && gameState.isBloomholdUnlocked() && isBloomholdEligible(req.itemId)) {
                    const bloomholdCount = gameState.getBloomholdItemCount(req.itemId);
                    if (bloomholdCount > 0) {
                        const fromBloomhold = Math.min(remaining, bloomholdCount);
                        gameState.removeBloomholdItem(req.itemId, fromBloomhold);
                        remaining -= fromBloomhold;
                        logger.debug(`[BOUNTY] Consumed ${fromBloomhold}x ${req.itemId} from BloomHold`);
                    }
                }

                if (remaining > 0) {
                    logger.warn(`Failed to remove ${remaining} ${req.itemId} for contract`);
                }
            }
            // Kill requirements don't consume anything
        }

        // Check for Jackpot rewards proc
        const jackpotProc = rollTalentProc(SKILLS.BOUNTY, 'doubleContractReward');
        let rewardMultiplier = 1;

        if (jackpotProc) {
            // Check for Fortune's Favor upgrade (can proc 5x instead of 2x)
            const hasFortuneFavor = getTalentFlatBonus(SKILLS.BOUNTY, 'fortuneFavor') > 0;
            if (hasFortuneFavor && Math.random() < 0.5) {
                // 50% chance for 5x when Fortune's Favor is unlocked
                rewardMultiplier = 5;
                recordTalentProc(SKILLS.BOUNTY, "Fortune's Favor", '5x rewards!', null);
            } else {
                rewardMultiplier = 2;
                recordTalentProc(SKILLS.BOUNTY, 'Jackpot', '2x rewards!', null);
            }
        }

        // Calculate reputation streak bonus
        const streakBonus = getTalentFlatBonus(SKILLS.BOUNTY, 'reputationStreak');
        const streakMultiplier = streakBonus > 0 ? (1 + (this.getReputationStreak() * streakBonus)) : 1;

        // Calculate bounty mark multiplier (kill contracts only)
        const markMultiplier = this.getMarkMultiplier(activeContract);

        // Calculate gold reward with all bonuses
        let goldReward = activeContract.rewards?.gold || 0;
        if (goldReward > 0) {
            const goldBonus = getTalentFlatBonus(SKILLS.BOUNTY, 'contractGoldBonus');

            // Golden Touch: +20% gold for T4+ contracts
            const highTierBonus = (activeContract.tier >= 4) ?
                getTalentFlatBonus(SKILLS.BOUNTY, 'highTierGoldBonus') : 0;

            let totalGoldMultiplier = 1;
            if (goldBonus > 0) totalGoldMultiplier += goldBonus;
            if (highTierBonus > 0) totalGoldMultiplier += highTierBonus;

            goldReward = Math.floor(goldReward * totalGoldMultiplier * streakMultiplier * rewardMultiplier * markMultiplier);

            gameState.addGold(goldReward);
            showGoldGain(goldReward);
            logger.game(`Received ${goldReward} gold from contract`);
        }

        // Award XP to relevant skills from stored rewards
        const xpRewards = {};
        if (activeContract.rewards?.xp) {
            for (const [skillId, baseXP] of Object.entries(activeContract.rewards.xp)) {
                if (gameState.getSkill(skillId)) {
                    const xpAmount = Math.floor(baseXP * streakMultiplier * rewardMultiplier * markMultiplier);
                    addXP(skillId, xpAmount);
                    xpRewards[skillId] = xpAmount;
                    logger.game(`Received ${xpAmount} ${skillId} XP from contract`);
                }
            }
        }

        // Award Bounty XP based on tier and type
        let bountyXP = getBountyXP(contract);

        // Apply Bounty talent XP bonuses
        const baseXPBonus = getTalentXPBonus(SKILLS.BOUNTY);

        // Apply Husbandry Pack Mentality bonus (+10% Bounty contract XP)
        const husbandryBountyBonus = getTalentFlatBonus(SKILLS.HUSBANDRY, 'bountyContractXP');

        let bountyXPMultiplier = 1;
        if (baseXPBonus > 0) bountyXPMultiplier += baseXPBonus;
        if (husbandryBountyBonus > 0) bountyXPMultiplier += husbandryBountyBonus;

        bountyXP = Math.floor(bountyXP * bountyXPMultiplier * streakMultiplier * rewardMultiplier * markMultiplier);

        const xpResult = addXP(SKILLS.BOUNTY, bountyXP);
        xpRewards.bounty = bountyXP;

        // Check for Surplus Finder proc (bonus crafting materials)
        if (rollTalentProc(SKILLS.BOUNTY, 'surplusMaterial')) {
            const surplus = this.getSurplusMaterial(activeContract.tier || 1);
            gameState.addItem(surplus.itemId, surplus.quantity);
            recordTalentProc(SKILLS.BOUNTY, 'Surplus Finder', null, null, { itemId: surplus.itemId, quantity: surplus.quantity });
            logger.game(`Surplus Finder proc! Received ${surplus.quantity} ${surplus.itemId}!`);
        }

        // Track contract type completion for Specialized Reduction talent
        this.incrementTypeCompletion(contract.type);

        // Increment reputation streak on successful delivery
        this.incrementReputationStreak();

        // Reset abandon count on successful delivery
        this.resetAbandonCount();

        // Bulk Processor: +10% progress to other active contracts
        const bulkBonus = getTalentFlatBonus(SKILLS.BOUNTY, 'bulkProcessor');
        if (bulkBonus > 0) {
            const otherContracts = this.getActiveContracts().filter(c => c.contractId !== contractId);
            for (const other of otherContracts) {
                for (const req of other.requirements) {
                    const reqId = req.itemId || req.mobId;
                    const bonusProgress = Math.floor(req.target * bulkBonus);
                    if (bonusProgress > 0) {
                        other.progress[reqId] = (other.progress[reqId] || 0) + bonusProgress;
                        logger.debug(`[BOUNTY] Bulk Processor: +${bonusProgress} progress to ${reqId}`);
                    }
                }
            }
        }

        // Remove from active contracts
        gameState.removeBountyContract(contractId);
        this.clearReadyNotification(contractId);

        // Regenerate this contract with new random targets for the board
        const contractKey = contractId.toUpperCase();
        delete this._generatedContracts[contractKey];
        this.getOrGenerateContract(contractKey);

        logger.game(`Completed contract: ${contract.name}`);

        if (this.onContractDelivered) {
            this.onContractDelivered(contract, { gold: goldReward, xp: xpRewards, xpResult });
        }

        return {
            success: true,
            rewards: {
                gold: goldReward,
                xp: xpRewards
            }
        };
    }

    // ==================== PROGRESS TRACKING ====================

    /**
     * Track progress for item collection
     * Called when player gains items through SKILL ACTIONS ONLY
     * NOT called for bank withdrawals, chest claims, or other non-skill sources
     * @param {string} itemId - Item ID that was gained
     * @param {number} quantity - Amount gained
     */
    trackItemProgress(itemId, quantity) {
        const activeContracts = this.getActiveContracts();

        for (const activeContract of activeContracts) {
            // Use stored requirements from active contract (includes adjusted targets)
            const requirement = activeContract.requirements?.find(r => r.itemId === itemId);
            if (requirement) {
                // Increment progress counter (like kills) - only counts items gained through skills
                const currentProgress = activeContract.progress[itemId] || 0;
                activeContract.progress[itemId] = currentProgress + quantity;

                // Check if contract just became ready
                this._checkContractReady(activeContract.contractId);
            }
        }
    }

    /**
     * Track progress for mob kills
     * Called from combat.js when a mob is killed
     * @param {string} mobId - Mob ID that was killed
     * @param {number} count - Number killed
     */
    trackKillProgress(mobId, count = 1) {
        const activeContracts = this.getActiveContracts();
        let updated = false;

        for (const activeContract of activeContracts) {
            // Check if this contract needs kills of this mob
            const requirement = activeContract.requirements?.find(r => r.mobId === mobId);
            if (requirement) {
                // Update kill count in progress
                const currentProgress = activeContract.progress[mobId] || 0;
                activeContract.progress[mobId] = currentProgress + count;
                updated = true;

                logger.debug(`[BOUNTY] Kill progress: ${mobId} ${activeContract.progress[mobId]}/${requirement.target}`);

                // Check if contract just became ready
                this._checkContractReady(activeContract.contractId);
            }
        }

        return updated;
    }

    /**
     * Check if a contract just became ready and fire callback
     * @param {string} contractId - Contract ID to check
     * @private
     */
    _checkContractReady(contractId) {
        // Skip if already notified for this contract
        if (this._notifiedReadyContracts.has(contractId)) {
            return;
        }

        // Check if contract can now be delivered
        const canDeliver = this.canDeliverContract(contractId);
        if (canDeliver.success) {
            // Mark as notified so we don't spam
            this._notifiedReadyContracts.add(contractId);

            // Fire callback
            if (this.onContractReady) {
                const contract = canDeliver.contract;
                this.onContractReady(contractId, contract);
            }
        }
    }

    /**
     * Clear ready notification for a contract (called when contract is delivered or abandoned)
     * @param {string} contractId - Contract ID
     */
    clearReadyNotification(contractId) {
        this._notifiedReadyContracts.delete(contractId);
    }

    /**
     * Check all active contracts for readiness (called from tick loop)
     */
    checkAllContractsReady() {
        const activeContracts = this.getActiveContracts();
        for (const active of activeContracts) {
            this._checkContractReady(active.contractId);
        }
    }

    /**
     * Get current progress for a contract requirement
     * @param {string} contractId - Contract ID
     * @param {string} requirementId - Item or mob ID
     * @returns {Object} { current, target, percent }
     */
    getRequirementProgress(contractId, requirementId) {
        const activeContract = this.getActiveContract(contractId);

        if (!activeContract || !activeContract.requirements) {
            return { current: 0, target: 0, percent: 0 };
        }

        // Use stored requirements from active contract
        const requirement = activeContract.requirements.find(
            r => r.itemId === requirementId || r.mobId === requirementId
        );

        if (!requirement) {
            return { current: 0, target: 0, percent: 0 };
        }

        // Progress is tracked explicitly via counters (both items and kills)
        let current = 0;
        const target = requirement.target;

        if (requirement.itemId) {
            // Item progress tracked via explicit trackItemProgress() calls
            current = activeContract.progress[requirement.itemId] || 0;
        } else if (requirement.mobId) {
            // Kill progress tracked via explicit trackKillProgress() calls
            current = activeContract.progress[requirement.mobId] || 0;
        }

        const percent = Math.min((current / target) * 100, 100);

        return { current, target, percent };
    }

    /**
     * Check overall completion percent for a contract
     * @param {string} contractId - Contract ID
     * @returns {number} 0-100 percent complete
     */
    getContractCompletionPercent(contractId) {
        const activeContract = this.getActiveContract(contractId);
        if (!activeContract || !activeContract.requirements) return 0;

        let totalPercent = 0;
        for (const req of activeContract.requirements) {
            const reqId = req.itemId || req.mobId;
            const progress = this.getRequirementProgress(contractId, reqId);
            totalPercent += progress.percent;
        }

        return totalPercent / activeContract.requirements.length;
    }

    /**
     * Get Contract Momentum bonus for a specific item or mob
     * Only applies while contract is active and requirement is NOT yet met
     * @param {string} targetId - Item ID or Mob ID to check
     * @returns {number} Bonus multiplier (e.g., 0.15 for +15%)
     */
    getContractMomentumBonus(targetId) {
        if (!targetId) return 0;

        // Check if Contract Momentum talent is unlocked
        const momentumBonus = getTalentFlatBonus(SKILLS.BOUNTY, 'contractMomentum');
        if (momentumBonus <= 0) return 0;

        // Check active contracts for this specific item/mob
        const activeContracts = this.getActiveContracts();
        for (const activeContract of activeContracts) {
            const requirement = activeContract.requirements?.find(
                r => r.itemId === targetId || r.mobId === targetId
            );

            if (requirement) {
                // Check if requirement is NOT yet complete
                const progress = this.getRequirementProgress(activeContract.contractId, targetId);
                if (progress.current < progress.target) {
                    // Contract has this item/mob and it's not complete - apply bonus
                    return momentumBonus;
                }
            }
        }

        return 0;
    }

    /**
     * Reduce the target count on a contract requirement
     * Used by pilfering guild contract assistance
     * @param {string} contractId - Contract ID
     * @param {number} requirementIndex - Index of requirement to reduce
     * @param {number} reduction - Amount to reduce target by
     * @returns {Object} { success, newTarget, oldTarget }
     */
    reduceContractTarget(contractId, requirementIndex, reduction) {
        const activeContract = this.getActiveContract(contractId);
        if (!activeContract || !activeContract.requirements) {
            return { success: false, reason: 'Contract not found' };
        }

        const requirement = activeContract.requirements[requirementIndex];
        if (!requirement) {
            return { success: false, reason: 'Requirement not found' };
        }

        const oldTarget = requirement.target;

        // Ensure we don't reduce below 1
        const newTarget = Math.max(1, oldTarget - reduction);

        // If no actual reduction would happen, skip
        if (newTarget >= oldTarget) {
            return { success: false, reason: 'No reduction possible' };
        }

        // Apply the reduction
        requirement.target = newTarget;

        logger.debug(`[BOUNTY] Contract assist: reduced target from ${oldTarget} to ${newTarget}`);

        return {
            success: true,
            oldTarget,
            newTarget,
            reduction: oldTarget - newTarget,
            requirementId: requirement.itemId || requirement.mobId
        };
    }

    // ==================== UTILITY ====================

    /**
     * Get contract definition by ID
     * @param {string} contractId - Contract ID
     * @returns {Object|null} Contract definition
     */
    getContractDefinition(contractId) {
        return PLAYER_CONTRACTS[contractId.toUpperCase()] || null;
    }

    /**
     * Clean up orphaned contracts (contracts with no matching definition)
     * Called on game load to handle contracts removed in updates
     * @returns {number} Number of contracts removed
     */
    cleanupOrphanedContracts() {
        const activeContracts = this.getActiveContracts();
        let removed = 0;

        for (const active of activeContracts) {
            const definition = this.getContractDefinition(active.contractId);
            if (!definition) {
                // Contract no longer exists - remove it
                gameState.removeBountyContract(active.contractId);
                logger.warn(`[BOUNTY] Removed orphaned contract: ${active.contractId}`);
                removed++;
            }
        }

        if (removed > 0) {
            logger.game(`Cleaned up ${removed} outdated bounty contract(s)`);
        }

        return removed;
    }

}

export const bountySkill = new BountySkill();
export default bountySkill;
